import { useMutation, useSuspenseQuery } from '@apollo/client'
import { withModalSuspense } from '@faceup/ui'
import type { ModalProps } from '@faceup/ui-base'
import type { FrontendPermissionType } from '@faceup/utils'
import type { JSX } from 'react'
import { graphql } from '../../__generated__'
import { useMotherId } from '../../providers'
import { defineMessages, FormattedMessage } from '../../TypedIntl'
import { AbstractMemberModal, type AccessKey, accesses } from './AbstractMemberModal'

const messages = defineMessages({
  title: 'Administration.settings.editUser.title',
  cantEditAccountOwner: 'Administration.settings.permissions.cantEditAccountOwner',
})

const query = {
  EditMemberModalData: graphql(`
    query EditMemberModalDataQuery($memberId: UUID!, $motherId: UUID!) {
      memberViewer {
        id
        isAccountOwner(motherId: $motherId)
        ...AbstractMemberModal_member
      }
      institution(organizationalUnitId: $motherId) {
        id
        ...AbstractMemberModal_institution
      }
      member(memberId: $memberId, motherId: $motherId) {
        id
        isAccountOwner(motherId: $motherId)
        name
        phone
        companyIds(motherId: $motherId)
        email
        keys {
          id
          permissions(motherId: $motherId) {
            id
            type
            enabled
            additionalData {
              id
              categoryIds
            }
          }
        }
      }
    }
  `),
}

const mutations = {
  EditMember: graphql(`
    mutation EditMemberMutation($input: EditMemberInput!, $motherId: UUID!) {
      editMember(input: $input) {
        member {
          id
          name
          companyIds(motherId: $motherId)
        }
      }
    }
  `),
}

type EditMemberModalProps = {
  memberId: string
  onCompleted: () => void
  onClose: () => void
} & Required<Pick<ModalProps, 'open'>>

const _EditMemberModal = ({ open, onClose, memberId, onCompleted }: EditMemberModalProps) => {
  const { getMotherId } = useMotherId()
  const { data } = useSuspenseQuery(query.EditMemberModalData, {
    variables: { memberId, motherId: getMotherId() },
  })

  const [editMember] = useMutation(mutations.EditMember, { onCompleted })

  const memberViewer = data.memberViewer
  const institution = data.institution
  const memberToEdit = data.member

  if (!memberToEdit || !institution) {
    onClose()
    return null
  }

  const isKredenc = !memberViewer

  const defaultValues = {
    email: memberToEdit.email ?? '',
    institutionIds: memberToEdit.companyIds,
    permissions: memberToEdit.keys.permissions,
  }

  return (
    <AbstractMemberModal
      modalVariant='edit'
      title={<FormattedMessage {...messages.title} />}
      member={memberViewer}
      institution={institution}
      open={open}
      onClose={onClose}
      overrideDisabledMessageForType={(
        permissionType: FrontendPermissionType,
        originalMessage: JSX.Element | null
      ) => {
        if (
          accesses.settings.subItems?.map(({ type }) => type).includes(permissionType) &&
          memberToEdit.isAccountOwner
        ) {
          return <FormattedMessage {...messages.cantEditAccountOwner} />
        }

        return originalMessage
      }}
      overrideDisabledMessageForSection={(
        section: AccessKey,
        originalMessage: JSX.Element | null
      ) => {
        if (section === 'settings' && memberToEdit.isAccountOwner) {
          return <FormattedMessage {...messages.cantEditAccountOwner} />
        }

        return originalMessage
      }}
      defaultValues={defaultValues}
      onSubmit={async values => {
        const result = await editMember({
          variables: {
            motherId: getMotherId(),
            input: {
              organizationalUnitIds: values.institutionIds,
              permissions: values.permissions,
              motherId: getMotherId(),
              memberId: memberToEdit.id,
              name: memberToEdit.name ?? '',
              ...(isKredenc && { email: values.email }),
            },
          },
        })

        if (result.errors) {
          return false
        }

        onClose()
        return true
      }}
    />
  )
}

export const EditMemberModal = withModalSuspense(_EditMemberModal)
